// Helper function untuk membuat action types
const createActionTypes = (prefix) => ({
    REQUEST: `REQUEST_${prefix}`,
    RECEIVE: `RECEIVE_${prefix}`,
    ERROR: `RECEIVE_${prefix}_ERROR`
});

// Action types yang disederhanakan
export const ACTION_TYPES = {
    CITIES: createActionTypes('CITIES'),
    DISTRICT: createActionTypes('DISTRICT'),
    SUBDISTRICT: createActionTypes('SUBDISTRICT'),
    RESIDENTIAL_CITIES: createActionTypes('RESIDENTIAL_CITIES'),
    RESIDENTIAL_DISTRICT: createActionTypes('RESIDENTIAL_DISTRICT'),
    RESIDENTIAL_SUBDISTRICT: createActionTypes('RESIDENTIAL_SUBDISTRICT'),
    CORRESPONDENCE_CITIES: createActionTypes('CORRESPONDENCE_CITIES'),
    NPWP_CITIES: createActionTypes('NPWP_CITIES'),
    NPWP_DISTRICT: createActionTypes('NPWP_DISTRICT'),
    NPWP_SUBDISTRICT: createActionTypes('NPWP_SUBDISTRICT'),
    BO_IDENTITY_CITIES: createActionTypes('BO_IDENTITY_CITIES'),
    BO_IDENTITY_DISTRICT: createActionTypes('BO_IDENTITY_DISTRICT'),
    BO_IDENTITY_SUBDISTRICT: createActionTypes('BO_IDENTITY_SUBDISTRICT'),
    BO_RESIDENTIAL_CITIES: createActionTypes('BO_RESIDENTIAL_CITIES'),
    BO_RESIDENTIAL_DISTRICT: createActionTypes('BO_RESIDENTIAL_DISTRICT'),
    BO_RESIDENTIAL_SUBDISTRICT: createActionTypes('BO_RESIDENTIAL_SUBDISTRICT'),
};

const initialState = {
    entities: [],
    residentialCitiesEntities: [],
    correspondenceCitiesEntities: [],
    districtEntities: [],
    subDistrictEntities: [],
    residentialDistrictEntities: [],
    residentialSubDistrictEntities: [],
    npwpCitiesEntities: [],
    npwpDistrictEntities: [],
    npwpSubDistrictEntities: [],
    boIdentityCitiesEntities: [],
    boIdentityDistrictEntities: [],
    boIdentitySubDistrictEntities: [],
    boResidentialCitiesEntities: [],
    boResidentialDistrictEntities: [],
    boResidentialSubDistrictEntities: [],
    isLoading: false,
    error: null
};

// Generic API call function
const apiCall = async (url) => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
};

// Generic action creator
const createAsyncAction = (actionTypes, urlGenerator, entityKey = 'entities') => {
    return (param) => async (dispatch) => {
        dispatch({ type: actionTypes.REQUEST });

        try {
            // Handle empty parameter case
            if (param === "" || param === null || param === undefined) {
                dispatch({
                    type: actionTypes.RECEIVE,
                    entities: null,
                    entityKey
                });
                return;
            }

            const url = urlGenerator(param);
            const entities = await apiCall(url);

            dispatch({
                type: actionTypes.RECEIVE,
                entities,
                entityKey
            });
        } catch (error) {
            console.error(`Error fetching data:`, error);
            dispatch({
                type: actionTypes.ERROR,
                error: error.message,
                entityKey
            });
        }
    };
};

// Simplified actions
export const cityActions = {
    // Cities
    requestCities: createAsyncAction(
        ACTION_TYPES.CITIES,
        (provinceId) => `api/city?id=${provinceId}`,
        'entities'
    ),

    // Districts
    requestDistrict: createAsyncAction(
        ACTION_TYPES.DISTRICT,
        (cityId) => `api/postalcode?cityId=${cityId}&tipe=district`,
        'districtEntities'
    ),

    // Subdistricts
    requestSubDistrict: createAsyncAction(
        ACTION_TYPES.SUBDISTRICT,
        (districtName) => `api/postalcode?districtName=${districtName}&tipe=subdistrict`,
        'subDistrictEntities'
    ),

    // Residential
    requestResidentialCities: createAsyncAction(
        ACTION_TYPES.RESIDENTIAL_CITIES,
        (provinceId) => `api/city?id=${provinceId}`,
        'residentialCitiesEntities'
    ),

    requestResidentialDistricts: createAsyncAction(
        ACTION_TYPES.RESIDENTIAL_DISTRICT,
        (cityId) => `api/postalcode?cityId=${cityId}&tipe=district`,
        'residentialDistrictEntities'
    ),

    requestResidentialSubDistricts: createAsyncAction(
        ACTION_TYPES.RESIDENTIAL_SUBDISTRICT,
        (districtName) => `api/postalcode?districtName=${districtName}&tipe=subdistrict`,
        'residentialSubDistrictEntities'
    ),

    // Correspondence
    requestCorrespondenceCities: createAsyncAction(
        ACTION_TYPES.CORRESPONDENCE_CITIES,
        (provinceId) => `api/city?id=${provinceId}`,
        'correspondenceCitiesEntities'
    ),

    // NPWP
    requestNPWPCities: createAsyncAction(
        ACTION_TYPES.NPWP_CITIES,
        (provinceId) => `api/city?id=${provinceId}`,
        'npwpCitiesEntities'
    ),

    requestNPWPDistricts: createAsyncAction(
        ACTION_TYPES.NPWP_DISTRICT,
        (cityId) => `api/postalcode?cityId=${cityId}&tipe=district`,
        'npwpDistrictEntities'
    ),

    requestNPWPSubDistricts: createAsyncAction(
        ACTION_TYPES.NPWP_SUBDISTRICT,
        (districtName) => `api/postalcode?districtName=${districtName}&tipe=subdistrict`,
        'npwpSubDistrictEntities'
    ),

    // BO Identity
    requestBOIdentityCities: createAsyncAction(
        ACTION_TYPES.BO_IDENTITY_CITIES,
        (provinceId) => `api/city?id=${provinceId}`,
        'boIdentityCitiesEntities'
    ),
    requestBOIdentityDistricts: createAsyncAction(
        ACTION_TYPES.BO_IDENTITY_DISTRICT,
        (cityId) => `api/postalcode?cityId=${cityId}&tipe=district`,
        'boIdentityDistrictEntities'
    ),
    requestBOIdentitySubDistricts: createAsyncAction(
        ACTION_TYPES.BO_IDENTITY_SUBDISTRICT,
        (districtName) => `api/postalcode?districtName=${districtName}&tipe=subdistrict`,
        'boIdentitySubDistrictEntities'
    ),

    // BO Residential
    requestBOResidentialCities: createAsyncAction(
        ACTION_TYPES.BO_RESIDENTIAL_CITIES,
        (provinceId) => `api/city?id=${provinceId}`,
        'boResidentialCitiesEntities'
    ),
    requestBOResidentialDistricts: createAsyncAction(
        ACTION_TYPES.BO_RESIDENTIAL_DISTRICT,
        (cityId) => `api/postalcode?cityId=${cityId}&tipe=district`,
        'boResidentialDistrictEntities'
    ),
    requestBOResidentialSubDistricts: createAsyncAction(
        ACTION_TYPES.BO_RESIDENTIAL_SUBDISTRICT,
        (districtName) => `api/postalcode?districtName=${districtName}&tipe=subdistrict`,
        'boResidentialSubDistrictEntities'
    ),
};

// Generic reducer handler
const createReducerHandler = (entityKey) => ({
    request: (state) => ({
        ...state,
        isLoading: true,
        error: null
    }),

    receive: (state, action) => ({
        ...state,
        isLoading: false,
        [entityKey]: action.entities,
        error: null
    }),

    error: (state, action) => ({
        ...state,
        isLoading: false,
        error: action.error
    })
});

// Mapping entity keys to their handlers
const entityHandlers = {
    'entities': createReducerHandler('entities'),
    'districtEntities': createReducerHandler('districtEntities'),
    'subDistrictEntities': createReducerHandler('subDistrictEntities'),
    'residentialCitiesEntities': createReducerHandler('residentialCitiesEntities'),
    'residentialDistrictEntities': createReducerHandler('residentialDistrictEntities'),
    'residentialSubDistrictEntities': createReducerHandler('residentialSubDistrictEntities'),
    'correspondenceCitiesEntities': createReducerHandler('correspondenceCitiesEntities'),
    'npwpCitiesEntities': createReducerHandler('npwpCitiesEntities'),
    'npwpDistrictEntities': createReducerHandler('npwpDistrictEntities'),
    'npwpSubDistrictEntities': createReducerHandler('npwpSubDistrictEntities'),
    'boIdentityCitiesEntities': createReducerHandler('boIdentityCitiesEntities'),
    'boIdentityDistrictEntities': createReducerHandler('boIdentityDistrictEntities'),
    'boIdentitySubDistrictEntities': createReducerHandler('boIdentitySubDistrictEntities'),
    'boResidentialCitiesEntities': createReducerHandler('boResidentialCitiesEntities'),
    'boResidentialDistrictEntities': createReducerHandler('boResidentialDistrictEntities'),
    'boResidentialSubDistrictEntities': createReducerHandler('boResidentialSubDistrictEntities'),
};

// Simplified reducer
export const reducer = (state = initialState, action) => {
    // Dynamically handle all action types
    for (const [prefix, actionTypes] of Object.entries(ACTION_TYPES)) {
        const entityKey = action.entityKey || getEntityKeyFromPrefix(prefix);
        const handlers = entityHandlers[entityKey];

        if (!handlers) continue;

        if (action.type === actionTypes.REQUEST) {
            return handlers.request(state);
        }

        if (action.type === actionTypes.RECEIVE) {
            return handlers.receive(state, action);
        }

        if (action.type === actionTypes.ERROR) {
            return handlers.error(state, action);
        }
    }

    return state;
};

// Helper function untuk mapping prefix ke entity key
function getEntityKeyFromPrefix(prefix) {
    const mapping = {
        'CITIES': 'entities',
        'DISTRICT': 'districtEntities',
        'SUBDISTRICT': 'subDistrictEntities',
        'RESIDENTIAL_CITIES': 'residentialCitiesEntities',
        'RESIDENTIAL_DISTRICT': 'residentialDistrictEntities',
        'RESIDENTIAL_SUBDISTRICT': 'residentialSubDistrictEntities',
        'CORRESPONDENCE_CITIES': 'correspondenceCitiesEntities',
        'NPWP_CITIES': 'npwpCitiesEntities',
        'NPWP_DISTRICT': 'npwpDistrictEntities',
        'NPWP_SUBDISTRICT': 'npwpSubDistrictEntities',
        'BO_IDENTITY_CITIES': 'boIdentityCitiesEntities',
        'BO_IDENTITY_DISTRICT': 'boIdentityDistrictEntities',
        'BO_IDENTITY_SUBDISTRICT': 'boIdentitySubDistrictEntities',
        'BO_RESIDENTIAL_CITIES': 'boResidentialCitiesEntities',
        'BO_RESIDENTIAL_DISTRICT': 'boResidentialDistrictEntities',
        'BO_RESIDENTIAL_SUBDISTRICT': 'boResidentialSubDistrictEntities',
    };

    return mapping[prefix] || 'entities';
}


// WEBPACK FOOTER //
// ./src/store/City.Reducer.js